import { useMemo } from "react";
import { Link, useSearch } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Package,
  GitCompareArrows,
  Trophy,
  XOctagon,
  Briefcase,
  Banknote,
  Target,
  Check,
  Minus,
  Layers,
} from "lucide-react";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}

function useQueryIds(): number[] {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const raw = params.get("ids") ?? "";
  return raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)
    .slice(0, 4);
}

function CrmProductCompareContent() {
  const ids = useQueryIds();

  const productsQuery = trpc.crm.products.getMany.useQuery(
    ids.length > 0 ? { ids } : (undefined as any),
    { enabled: ids.length > 0 },
  );

  // Carrega stats de cada produto em paralelo (um query hook por produto).
  // Como hooks não podem estar em loops dinâmicos, usamos um wrapper.
  const statsA = trpc.crm.products.stats.useQuery(
    ids[0] ? { productId: ids[0] } : (undefined as any),
    { enabled: !!ids[0] },
  );
  const statsB = trpc.crm.products.stats.useQuery(
    ids[1] ? { productId: ids[1] } : (undefined as any),
    { enabled: !!ids[1] },
  );
  const statsC = trpc.crm.products.stats.useQuery(
    ids[2] ? { productId: ids[2] } : (undefined as any),
    { enabled: !!ids[2] },
  );
  const statsD = trpc.crm.products.stats.useQuery(
    ids[3] ? { productId: ids[3] } : (undefined as any),
    { enabled: !!ids[3] },
  );
  const statsByPos = [statsA, statsB, statsC, statsD];

  const rows = productsQuery.data ?? [];

  // Mantém a ordem dos IDs passados na URL.
  const ordered = useMemo(() => {
    return ids
      .map((id) => rows.find((r: any) => r.product.id === id))
      .filter(Boolean) as any[];
  }, [ids, rows]);

  // Preço pelo qual o produto realmente entra na comparação: o menor entre as
  // ofertas ativas, ou o preço sugerido quando não há oferta.
  function effectivePrice(row: any): string {
    return row.offerCount > 0 && row.offerMinPrice != null
      ? row.offerMinPrice
      : row.product.price;
  }

  // Coleta todas as chaves de atributos (união) para montar a tabela comparativa.
  const allAttrKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of ordered) {
      const attrs = (row.product.attributes ?? {}) as Record<string, any>;
      Object.keys(attrs).forEach((k) => keys.add(k));
    }
    return Array.from(keys);
  }, [ordered]);

  // Determina quem tem menor preço / maior win rate (para destaque).
  const highlights = useMemo(() => {
    if (ordered.length === 0) return { minPriceId: null, bestWinId: null };
    let minPrice = Infinity;
    let minPriceId: number | null = null;
    let bestWin = -1;
    let bestWinId: number | null = null;
    ordered.forEach((row, idx) => {
      // Produto com ofertas é comparado pelo MENOR preço vigente — comparar o
      // preço sugerido dele contra o preço real de outro daria "melhor preço"
      // para o lado errado.
      const price = Number(effectivePrice(row));
      if (!Number.isNaN(price) && price > 0 && price < minPrice) {
        minPrice = price;
        minPriceId = row.product.id;
      }
      const s = statsByPos[idx]?.data;
      const total = (s?.wonDeals ?? 0) + (s?.lostDeals ?? 0);
      if (total > 0) {
        const wr = (s?.wonDeals ?? 0) / total;
        if (wr > bestWin) {
          bestWin = wr;
          bestWinId = row.product.id;
        }
      }
    });
    return { minPriceId, bestWinId };
  }, [ordered, statsByPos]);

  if (ids.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <GitCompareArrows className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <h3 className="font-semibold mb-1">Nada para comparar</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Selecione 2 a 4 produtos no catálogo para comparar.
          </p>
          <Link href="/crm/products">
            <Button>
              <Package className="h-4 w-4 mr-1.5" />
              Abrir catálogo
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <GitCompareArrows className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
              Comparar produtos
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {ids.length} selecionado{ids.length > 1 ? "s" : ""} · lado a lado
            </p>
          </div>
        </div>
        <Link href="/crm/products">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Voltar ao catálogo
          </Button>
        </Link>
      </div>

      {productsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: ids.length }).map((_, i) => (
            <Skeleton key={i} className="h-96 w-full" />
          ))}
        </div>
      ) : ordered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            Produtos não encontrados.
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div
            className="grid gap-3 min-w-[640px]"
            style={{
              gridTemplateColumns: `160px repeat(${ordered.length}, minmax(220px, 1fr))`,
            }}
          >
            {/* ── Header row (hero de cada produto) ─ */}
            <div />
            {ordered.map((row: any) => (
              <div key={row.product.id} className="space-y-3">
                <Link
                  href={`/crm/products/${row.product.id}`}
                  className="block rounded-lg border bg-card overflow-hidden hover:shadow-md transition"
                >
                  <div className="aspect-[4/3] bg-muted overflow-hidden">
                    {row.product.imageUrl ? (
                      <img
                        src={row.product.imageUrl}
                        alt={row.product.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                        <Package className="h-10 w-10 opacity-30" />
                      </div>
                    )}
                  </div>
                  <div className="p-3 space-y-1.5">
                    {row.category?.name && (
                      <Badge
                        className="text-[10px]"
                        style={{
                          backgroundColor: row.category.color ?? "#64748b",
                          color: "white",
                        }}
                      >
                        {row.category.name}
                      </Badge>
                    )}
                    <div className="text-sm font-semibold line-clamp-2 leading-tight">
                      {row.product.name}
                    </div>
                    {row.product.sku && (
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {row.product.sku}
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}

            {/* ── Row: Preço ─────────────────────── */}
            <CompareRowHeader icon={Banknote} label="Preço" />
            {ordered.map((row: any) => (
              <CompareCell
                key={row.product.id}
                highlight={highlights.minPriceId === row.product.id}
                highlightLabel="Melhor preço"
                value={
                  <div>
                    {row.offerCount > 0 && (
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        A partir de
                      </div>
                    )}
                    <span className="text-lg font-bold tabular-nums">
                      {formatBRL(effectivePrice(row))}
                    </span>
                  </div>
                }
              />
            ))}

            {/* ── Row: Ofertas ───────────────────── */}
            {/* É exatamente a pergunta lado-a-lado que esta página existe para
                responder: quais formatos cada produto tem, e por quanto. */}
            <CompareRowHeader icon={Layers} label="Ofertas" />
            {ordered.map((row: any) => (
              <CompareCell
                key={row.product.id}
                value={
                  (row.offers?.length ?? 0) === 0 ? (
                    <Minus className="h-4 w-4 text-muted-foreground/50 mx-auto" />
                  ) : (
                    <div className="space-y-0.5 text-left">
                      {row.offers.map((o: any) => (
                        <div key={o.id} className="flex items-baseline gap-1.5 text-xs">
                          <span className="truncate">{o.name}</span>
                          <span className="tabular-nums text-muted-foreground shrink-0">
                            {formatBRL(o.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                }
              />
            ))}

            {/* ── Row: Em oportunidades ──────────── */}
            <CompareRowHeader icon={Briefcase} label="Em oportunidades" />
            {ordered.map((row: any, idx: number) => (
              <CompareCell
                key={row.product.id}
                value={
                  <span className="text-lg font-semibold tabular-nums">
                    {statsByPos[idx]?.data?.totalDeals ?? 0}
                  </span>
                }
              />
            ))}

            {/* ── Row: Fechadas ───────────────────── */}
            <CompareRowHeader icon={Trophy} label="Fechadas" />
            {ordered.map((row: any, idx: number) => {
              const s = statsByPos[idx]?.data;
              return (
                <CompareCell
                  key={row.product.id}
                  value={
                    <span className="text-lg font-semibold tabular-nums text-emerald-600">
                      {s?.wonDeals ?? 0}
                    </span>
                  }
                />
              );
            })}

            {/* ── Row: Perdidas ──────────────────── */}
            <CompareRowHeader icon={XOctagon} label="Perdidas" />
            {ordered.map((row: any, idx: number) => {
              const s = statsByPos[idx]?.data;
              return (
                <CompareCell
                  key={row.product.id}
                  value={
                    <span className="text-lg font-semibold tabular-nums text-red-600">
                      {s?.lostDeals ?? 0}
                    </span>
                  }
                />
              );
            })}

            {/* ── Row: Win rate ──────────────────── */}
            <CompareRowHeader icon={Target} label="Win rate" />
            {ordered.map((row: any, idx: number) => {
              const s = statsByPos[idx]?.data;
              const total = (s?.wonDeals ?? 0) + (s?.lostDeals ?? 0);
              const wr = total > 0 ? ((s?.wonDeals ?? 0) / total) * 100 : null;
              return (
                <CompareCell
                  key={row.product.id}
                  highlight={highlights.bestWinId === row.product.id && wr !== null}
                  highlightLabel="Melhor conversão"
                  value={
                    <span className="text-lg font-semibold tabular-nums">
                      {wr === null ? "—" : `${wr.toFixed(0)}%`}
                    </span>
                  }
                />
              );
            })}

            {/* ── Row: Receita ganha ─────────────── */}
            <CompareRowHeader icon={Banknote} label="Receita ganha" />
            {ordered.map((row: any, idx: number) => {
              const s = statsByPos[idx]?.data;
              return (
                <CompareCell
                  key={row.product.id}
                  value={
                    <span className="text-base font-semibold tabular-nums">
                      {formatCompact(s?.wonRevenue ?? 0)}
                    </span>
                  }
                />
              );
            })}

            {/* ── Descrição ──────────────────────── */}
            <CompareRowHeader label="Descrição" />
            {ordered.map((row: any) => (
              <CompareCell
                key={row.product.id}
                value={
                  row.product.description ? (
                    <p className="text-xs text-muted-foreground line-clamp-6 whitespace-pre-wrap">
                      {row.product.description}
                    </p>
                  ) : (
                    <Minus className="h-4 w-4 text-muted-foreground" />
                  )
                }
              />
            ))}

            {/* ── Atributos (união) ──────────────── */}
            {allAttrKeys.length > 0 && (
              <>
                <div className="col-span-full pt-3 pb-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-2">
                    Diferenciais
                  </div>
                </div>
                {allAttrKeys.map((key) => (
                  <AttributeRow key={key} attrKey={key} products={ordered} />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CompareRowHeader({ label, icon: Icon }: { label: string; icon?: any }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-t pt-3 first:border-t-0 first:pt-0">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </div>
  );
}

function CompareCell({
  value,
  highlight = false,
  highlightLabel,
}: {
  value: React.ReactNode;
  highlight?: boolean;
  highlightLabel?: string;
}) {
  return (
    <div
      className={`rounded-md border-t pt-3 first:border-t-0 first:pt-0 relative ${
        highlight ? "bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-3 py-2 border border-emerald-200 dark:border-emerald-900" : ""
      }`}
    >
      {value}
      {highlight && highlightLabel && (
        <Badge className="bg-emerald-600 absolute -top-2 right-2 text-[9px] h-4">
          <Check className="h-2.5 w-2.5 mr-0.5" />
          {highlightLabel}
        </Badge>
      )}
    </div>
  );
}

function AttributeRow({ attrKey, products }: { attrKey: string; products: any[] }) {
  const values = products.map((p) => {
    const attrs = (p.product.attributes ?? {}) as Record<string, any>;
    return attrs[attrKey];
  });
  return (
    <>
      <div className="flex items-center text-xs text-muted-foreground font-medium border-t pt-2">
        {attrKey}
      </div>
      {values.map((v, idx) => (
        <div key={idx} className="flex items-center border-t pt-2 text-sm">
          {v === undefined || v === null || v === "" ? (
            <Minus className="h-4 w-4 text-muted-foreground" />
          ) : (
            <span className="break-words">{String(v)}</span>
          )}
        </div>
      ))}
    </>
  );
}

export default function CrmProductCompare() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:products_view">
        <CrmProductCompareContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
